/**
 * Inline, theme-safe, and accessible bar chart — without heavy libraries.
 *
 * Renders bars with `div`s (height/width relative to maximum) using theme
 * color tokens (`bg-accent`, `bg-primary`…). Accessible: the container exposes
 * `role="img"` + `aria-label` with the summary, and each bar includes its value in
 * text for screen readers. Animation with `motion-safe`.
 *
 * `orientation`:
 *  - "vertical": columns (ideal for time series: activity per day).
 *  - "horizontal": bars (ideal for rankings: most linked pages).
 */
import { For, type JSX, Show } from "solid-js";
import { cn } from "@folio/shared";

export interface BarDatum {
  /** Axis label (short). */
  label: string;
  /** Numeric value (≥ 0). */
  value: number;
  /** Optional text for tooltip/aria (e.g. full date). */
  title?: string;
}

interface BarChartProps {
  data: BarDatum[];
  orientation?: "vertical" | "horizontal";
  /** Accessible label for the entire chart. */
  ariaLabel: string;
  /** Bar color class (theme token). */
  barClass?: string;
  /** Formats displayed value (by default, the number). */
  formatValue?: (value: number) => string;
  class?: string;
  /** Bar area height (vertical orientation only). */
  height?: number;
}

const DEFAULT_HEIGHT = 160;
const MIN_BAR_FRACTION = 0.04; // bars with value > 0 are always visible

export function BarChart(props: BarChartProps): JSX.Element {
  const orientation = () => props.orientation ?? "vertical";
  const max = () => Math.max(1, ...props.data.map((d) => d.value));
  const format = (value: number) => (props.formatValue ? props.formatValue(value) : String(value));
  const barColor = () => props.barClass ?? "bg-accent";

  const fraction = (value: number): number => {
    if (value <= 0) {
      return 0;
    }
    return Math.max(MIN_BAR_FRACTION, value / max());
  };

  return (
    <div role="img" aria-label={props.ariaLabel} class={cn("w-full", props.class)}>
      <Show
        when={orientation() === "vertical"}
        fallback={
          <ul class="flex flex-col gap-2.5">
            <For each={props.data}>
              {(d) => (
                <li
                  class="flex items-center gap-3"
                  title={d.title ?? `${d.label}: ${format(d.value)}`}
                >
                  <span class="w-28 shrink-0 truncate text-right text-xs text-muted-foreground">
                    {d.label}
                  </span>
                  <div class="relative h-5 flex-1 overflow-hidden rounded-md bg-muted/30">
                    {/* GPU-first: ocupa todo el ancho y escala con `transform:
                        scaleX` (compositor) en vez de animar `width` (layout). */}
                    <div
                      class={cn(
                        "h-full w-full origin-left rounded-md motion-safe:transition-transform motion-safe:duration-500 motion-safe:will-change-transform",
                        barColor(),
                      )}
                      style={{ transform: `scaleX(${fraction(d.value).toFixed(3)})` }}
                    />
                  </div>
                  <span class="w-10 shrink-0 text-right text-xs font-semibold tabular-nums text-foreground">
                    {format(d.value)}
                  </span>
                </li>
              )}
            </For>
          </ul>
        }
      >
        <div
          class="flex items-end gap-1.5"
          style={{ height: `${props.height ?? DEFAULT_HEIGHT}px` }}
        >
          <For each={props.data}>
            {(d) => (
              <div class="flex min-w-0 flex-1 flex-col items-center justify-end gap-1.5">
                <div
                  class="flex w-full items-end justify-center"
                  style={{ height: "100%" }}
                  title={d.title ?? `${d.label}: ${format(d.value)}`}
                >
                  {/* GPU-first: ocupa todo el alto y escala con `transform:
                      scaleY` (compositor) en vez de animar `height` (layout). */}
                  <div
                    class={cn(
                      "h-full w-full max-w-8 origin-bottom rounded-t-md motion-safe:transition-transform motion-safe:duration-500 motion-safe:will-change-transform",
                      d.value > 0 ? barColor() : "bg-muted/30",
                    )}
                    style={{
                      transform: `scaleY(${Math.max(fraction(d.value), 0.0001).toFixed(4)})`,
                    }}
                  />
                </div>
                <span class="w-full truncate text-center text-[10px] text-muted-foreground">
                  {d.label}
                </span>
              </div>
            )}
          </For>
        </div>
      </Show>
      {/* Tabla equivalente solo para lectores de pantalla. */}
      <table class="sr-only">
        <tbody>
          <For each={props.data}>
            {(d) => (
              <tr>
                <th scope="row">{d.title ?? d.label}</th>
                <td>{format(d.value)}</td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  );
}
